// What the `app_status` row (0383) means for an app right now (2026-10-30).
//
// Pure, so each app reads the row its own way (its own Supabase client) and
// every app draws the same conclusion from it:
//
//   off       nothing to show
//   upcoming  a heads-up, from two hours before the start
//   on        maintenance: no new bookings; jobs already running carry on
//   overdue   still on, past the time people were told
//
// `needsUpdate`: this build is older than the minimum the Super Admin set.

export interface AppStatusRow {
  maintenance_starts_at: string | null;
  maintenance_back_by: string | null;
  maintenance_message: string | null;
  min_build_at: string | null;
}

export type MaintenancePhase = "off" | "upcoming" | "on" | "overdue";

export interface AppStatus {
  phase: MaintenancePhase;
  startsAt: Date | null;
  backBy: Date | null;
  message: string | null;
  needsUpdate: boolean;
}

/** How long before a scheduled start the heads-up shows. */
export const HEADS_UP_MS = 2 * 60 * 60 * 1000;

const toDate = (value: string | null) => (value ? new Date(value) : null);

export function readAppStatus(
  row: AppStatusRow | null | undefined,
  buildAt: string,
  now: number = Date.now(),
): AppStatus {
  const startsAt = toDate(row?.maintenance_starts_at ?? null);
  const backBy = toDate(row?.maintenance_back_by ?? null);
  const minBuild = toDate(row?.min_build_at ?? null);

  let phase: MaintenancePhase = "off";
  if (startsAt) {
    const until = startsAt.getTime() - now;
    if (until > HEADS_UP_MS) phase = "off";
    else if (until > 0) phase = "upcoming";
    else if (backBy && backBy.getTime() < now) phase = "overdue";
    else phase = "on";
  }

  return {
    phase,
    startsAt,
    backBy,
    message: row?.maintenance_message?.trim() || null,
    needsUpdate: minBuild !== null && new Date(buildAt).getTime() < minBuild.getTime(),
  };
}

/** True while new bookings are paused. */
export function maintenanceActive(status: Pick<AppStatus, "phase">): boolean {
  return status.phase === "on" || status.phase === "overdue";
}

/** "4:00 PM", or "Tomorrow, 1:00 AM" / "Nov 2, 9:00 AM" when not today. Manila time. */
export function maintenanceTimeLabel(date: Date, now: number = Date.now()): string {
  const zone = "Asia/Manila";
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: zone,
  });
  const day = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: zone });
  if (day(date) === day(new Date(now))) return time;
  if (day(date) === day(new Date(now + 24 * 60 * 60 * 1000))) return `Tomorrow, ${time}`;
  const dayLabel = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: zone,
  });
  return `${dayLabel}, ${time}`;
}

/** Share of the window gone, 0–1, for the progress bar. */
export function maintenanceProgress(status: AppStatus, now: number = Date.now()): number {
  if (!status.startsAt || !status.backBy) return 0;
  const span = status.backBy.getTime() - status.startsAt.getTime();
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (now - status.startsAt.getTime()) / span));
}
